/**
 * In-memory mock backend for the Knowledge Base.
 *
 * Lets you run the frontend on localhost with no ACC Lambda / DynamoDB. It
 * mirrors the real backend command names and the { success, data } envelope
 * that `KnowledgeUtils` expects, and keeps mutable state in module scope so
 * create / edit / delete / acknowledge / assign all work within a session
 * (state resets on full page reload).
 *
 * Enable it either way:
 *   - build/start time:  REACT_APP_KNOWLEDGE_MOCK=true npm start
 *   - at runtime:        localStorage.setItem('kb_mock', 'true'); location.reload();
 *   - disable runtime:   localStorage.removeItem('kb_mock'); location.reload();
 */

import { getUserName } from '../Utils/GeneralUtils';
import { deriveStepsFromBody, normalizeSopSteps } from './sopSteps';
import { normalizeLesson } from './lessonBlock';
import { LESSON_FOLDER, seedLessonArticles } from './mockLessonSeeds';
import { GUIDE_FOLDER, seedGuideArticles } from './mockGuideSeeds';

export { isMockMode } from './mockMode';

const currentUser = () => {
    try {
        return getUserName() || 'Demo Operator';
    } catch (e) {
        return 'Demo Operator';
    }
};

const NETWORK_DELAY_MS = 120;

const iso = (daysFromNow = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return d.toISOString();
};

let idSeq = 100;
const nextId = (prefix) => `${prefix}-${(idSeq += 1)}`;

// --- Seed data -------------------------------------------------------------

const seedFolders = () => [
    { id: 'f-onboarding', name: 'Onboarding', parentId: null },
    { id: 'f-sops', name: 'SOPs', parentId: null },
    { id: 'f-sops-cancellations', name: 'Cancellations', parentId: 'f-sops' },
    { id: 'f-troubleshooting', name: 'Troubleshooting', parentId: null },
    { id: 'f-reference', name: 'Reference', parentId: null },
    { ...GUIDE_FOLDER },
    { ...LESSON_FOLDER },
];

/**
 * A guided SOP fixture: steps derived from the body exactly the way
 * `scripts/derive_sop_steps.py` fills them server-side, with one step promoted
 * to photo evidence so the walkthrough exercises the camera path offline.
 */
const guidedSop = ({ id, title, body, photoStepId, photoPrompt, whenNeeded = 'during-shift' }) => ({
    id,
    title,
    folderId: 'f-sops',
    status: 'published',
    whenNeeded,
    requiredReading: false,
    visibility: [],
    version: 2,
    updatedAt: iso(-4),
    updatedBy: 'Jordan (Manager)',
    externalLinks: [],
    body,
    steps: normalizeSopSteps(deriveStepsFromBody(body)).map((step) =>
        (step.id === photoStepId
            ? { ...step, evidence: { ...step.evidence, method: 'photo', photoPrompt } }
            : step)),
});

const seedArticles = () => [
    {
        id: 'a-welcome',
        title: 'Welcome & First Shift Checklist',
        folderId: 'f-onboarding',
        status: 'published',
        whenNeeded: 'before-shift',
        requiredReading: true,
        visibility: [],
        version: 3,
        updatedAt: iso(-12),
        updatedBy: 'Jordan (Manager)',
        externalLinks: [],
        body: [
            '# Welcome to the floor',
            '',
            'Glad to have you. Before your first shift, make sure you can check every box below.',
            '',
            '## Before you clock in',
            '- [ ] Uniform on, name tag visible',
            '- [ ] Reviewed the **Safety & Emergency Procedures** article',
            '- [ ] Know where the first-aid kit and e-stops are',
            '',
            '## During your first shift',
            '1. Shadow the on-shift lead for the first hour.',
            '2. Run one full guest cycle (check-in → brief → play → reset).',
            '3. Ask questions early and often — nobody expects perfection on day one.',
            '',
            '> If anything here is unclear, flag it as out of date and a manager will fix it.',
        ].join('\n'),
    },
    {
        id: 'a-safety',
        title: 'Safety & Emergency Procedures',
        folderId: 'f-onboarding',
        status: 'published',
        whenNeeded: 'before-shift',
        requiredReading: true,
        visibility: [],
        version: 7,
        updatedAt: iso(-30),
        updatedBy: 'Jordan (Manager)',
        externalLinks: [],
        body: [
            '# Safety first, always',
            '',
            '## Emergency stops',
            'Every play area has a red **E-STOP**. Hitting it cuts motion immediately. Use it any time a guest could be hurt.',
            '',
            '## Evacuation',
            '1. Stay calm, raise your voice clearly.',
            '2. Guide guests to the nearest marked exit.',
            '3. Meet at the muster point in the north parking lot.',
            '',
            '## Medical',
            'Call a manager on radio channel 2 and do not move an injured guest unless they are in further danger.',
        ].join('\n'),
    },
    {
        id: 'a-pos',
        title: 'Using the POS Terminal',
        folderId: 'f-onboarding',
        status: 'published',
        whenNeeded: 'during-shift',
        requiredReading: false,
        visibility: [],
        version: 2,
        updatedAt: iso(-5),
        updatedBy: 'Sam (HeroTrainer)',
        externalLinks: [
            { label: 'Walkthrough: Process a walk-in sale', url: 'https://scribehow.com/shared/demo-pos-sale', kind: 'scribe' },
        ],
        body: [
            '# Point-of-sale basics',
            '',
            'The POS handles walk-ins, waivers, and add-ons. Follow the Scribe above for the click-by-click flow.',
            '',
            '## Quick tips',
            '- Always attach the waiver before taking payment.',
            '- For split payments, tap **Add tender** before closing the order.',
            '- Refunds go through the **Issuing Refunds** SOP — do not free-hand them.',
        ].join('\n'),
    },
    {
        id: 'a-cancel-save',
        title: 'Cancellation-Save SOP',
        folderId: 'f-sops-cancellations',
        status: 'published',
        whenNeeded: 'during-shift',
        requiredReading: true,
        visibility: [],
        version: 5,
        updatedAt: iso(-2),
        updatedBy: 'Jordan (Manager)',
        externalLinks: [
            { label: 'Retention offer matrix', url: 'https://example.com/retention-matrix', kind: 'external' },
        ],
        body: [
            '# Saving a cancellation',
            '',
            'When a member wants to cancel, the goal is to understand *why* and offer the right save.',
            '',
            '## Steps',
            '1. **Listen first.** Let them explain the reason without interrupting.',
            '2. **Categorize** the reason: price, usage, experience, or life change.',
            '3. **Offer** the matching save from the retention matrix (linked above).',
            '4. If they still cancel, process it cleanly and thank them.',
            '',
            '| Reason | First offer |',
            '| --- | --- |',
            '| Price | 1 month at 50% |',
            '| Usage | Free guest passes |',
            '| Experience | Manager callback |',
        ].join('\n'),
    },
    {
        id: 'a-refund',
        title: 'Issuing Refunds',
        folderId: 'f-sops',
        status: 'published',
        whenNeeded: 'when-x-breaks',
        requiredReading: false,
        visibility: ['Manager', 'Admin'],
        version: 4,
        updatedAt: iso(-9),
        updatedBy: 'Jordan (Manager)',
        externalLinks: [
            { label: 'Walkthrough: Issue a partial refund', url: 'https://scribehow.com/shared/demo-refund', kind: 'scribe' },
        ],
        body: [
            '# Refund policy & process',
            '',
            '> Visible to Managers and Admins only — this article is gated by `visibility`.',
            '',
            'Refunds over $100 require manager approval. Document the reason in the order notes every time.',
        ].join('\n'),
    },
    {
        id: 'a-vr-reboot',
        title: "VR Headset Won't Track — Reboot Steps",
        folderId: 'f-troubleshooting',
        status: 'published',
        whenNeeded: 'when-x-breaks',
        requiredReading: false,
        visibility: [],
        version: 6,
        updatedAt: iso(-1),
        updatedBy: 'Sam (HeroTrainer)',
        externalLinks: [
            { label: 'Video: Re-pair a controller', url: 'https://scribehow.com/shared/demo-vr-pair', kind: 'scribe' },
        ],
        body: [
            '# Headset tracking fix',
            '',
            'If a headset drifts or loses tracking mid-session:',
            '',
            '1. Ask the guest to hold still and look forward.',
            '2. Press and hold the headset power button for 3 seconds to recenter.',
            '3. If that fails, swap to a spare headset and reboot the bad one fully.',
            '4. Log the unit number so we can track repeat offenders.',
        ].join('\n'),
    },
    {
        id: 'a-network',
        title: 'Network Outage Playbook',
        folderId: 'f-troubleshooting',
        status: 'draft',
        whenNeeded: 'when-x-breaks',
        requiredReading: false,
        visibility: [],
        version: 1,
        updatedAt: iso(-1),
        updatedBy: 'Sam (HeroTrainer)',
        externalLinks: [],
        body: [
            '# When the network goes down',
            '',
            '_Draft — still being reviewed._',
            '',
            'Switch the POS to offline mode and keep paper waivers at the desk until connectivity returns.',
        ].join('\n'),
    },
    guidedSop({
        id: 'sop-gym-opening',
        title: 'SOP: Gym Opening',
        whenNeeded: 'before-shift',
        body: [
            '# SOP: Gym Opening',
            '',
            '## Purpose',
            'Open a floor that is ready before the first member walks in.',
            '',
            '## Procedure',
            '',
            '### 1. Disarm and unlock',
            '1. Enter your **ADT code** at the back panel.',
            '2. Unlock the front door and prop it for deliveries only.',
            '',
            '### 2. Boot the booths',
            '- Power every booth PC and connect its headset.',
            '- Watch each booth reach the lobby screen.',
            '',
            '## End state',
            'Lights on, every booth in the lobby, door unlocked.',
        ].join('\n'),
    }),
    guidedSop({
        id: 'sop-booth-prep-for-member-workout',
        title: 'SOP: Booth Prep for Member Workout',
        body: [
            '# SOP: Booth Prep for Member Workout',
            '',
            '## Purpose',
            'Hand a member a booth that is clean, calibrated and ready to play.',
            '',
            '## Procedure',
            '',
            '### 1. Wipe and reset',
            '- Wipe the headset, controllers and the cable arm.',
            '- Return the cable arm to its rest position.',
            '',
            '### 2. Check tracking',
            '1. Put the headset on and confirm the guardian boundary.',
            '2. Swing both controllers through the full range and watch for drift.',
            '',
            '## End state',
            'Booth idle on the lobby screen with clean gear on the hooks.',
        ].join('\n'),
    }),
    guidedSop({
        id: 'sop-advanced-alignment-troubleshooting',
        title: 'SOP: Advanced Alignment Troubleshooting',
        body: [
            '# SOP: Advanced Alignment Troubleshooting',
            '',
            '## Purpose',
            'Bring a drifting or misaligned booth back without a support ticket.',
            '',
            '## Procedure',
            '',
            '### 1. Re-seat the sensors',
            '- Check every sensor cable at the PC and at the frame.',
            '- Re-run the room setup from the booth menu.',
            '',
            '### 2. Confirm with a test set',
            '1. Run one set of the alignment drill.',
            '2. Escalate in the booth channel if the drift returns within the set.',
            '',
            '## End state',
            'Drill completes with no drift; booth back in rotation.',
        ].join('\n'),
    }),
    guidedSop({
        id: 'sop-gym-closing',
        title: 'SOP: Gym Closing',
        body: [
            '# SOP: Gym Closing',
            '',
            '## Purpose',
            'Shut the gym down cleanly so the opener walks into a ready floor.',
            '',
            '## Procedure',
            '',
            '### 1. Closing tasks',
            '- Complete the **closing tasks as noted in ACC**.',
            '- Includes the cleaning cadence for the day.',
            '',
            '### 2. Plug in the iPads',
            '1. Cable into every iPad at the desk.',
            '2. Screen or indicator shows it is charging.',
            '',
            '### 3. Arm the alarm',
            '1. Go to the **back** and enter your **ADT code**.',
            '2. Select **Arm Away** and leave within 60 seconds.',
            '',
            '### 4. Lock up',
            '- **Lock the door** with the hex key and pull to check.',
            '',
            '## End state',
            'Lights off, alarm armed, door locked.',
        ].join('\n'),
        photoStepId: 'plug-in-the-ipads',
        photoPrompt: 'The iPads at the desk with cables plugged in',
    }),
    guidedSop({
        id: 'sop-deep-cleaning-a-bathroom',
        title: 'SOP: Deep Cleaning a Bathroom',
        whenNeeded: 'during-shift',
        body: [
            '# SOP: Deep Cleaning a Bathroom',
            '',
            '## Purpose',
            'The weekly deep clean, top to bottom, so nothing gets missed.',
            '',
            '## Procedure',
            '',
            '### 1. Clear and restock',
            '- Empty the bin, replace the liner.',
            '- Restock paper, soap and seat covers.',
            '',
            '### 2. Scrub the toilet and sink',
            '1. Bowl cleaner in, let it sit while you do the sink.',
            '2. Scrub the bowl, seat and base.',
            '3. Wipe the sink, taps and the mirror.',
            '',
            '### 3. Mop the floor',
            '- Work from the far corner back to the door.',
            '',
            '### 4. Final look',
            '- Stand at the door and check the room the way a member sees it.',
            '',
            '## End state',
            'Dry floor, stocked shelves, nothing on the counters.',
        ].join('\n'),
        photoStepId: 'final-look',
        photoPrompt: 'One wide shot of the finished bathroom from the door',
    }),
    {
        id: 'a-glossary',
        title: 'Glossary of Terms',
        folderId: 'f-reference',
        status: 'published',
        whenNeeded: 'reference',
        requiredReading: false,
        visibility: [],
        version: 2,
        updatedAt: iso(-40),
        updatedBy: 'Jordan (Manager)',
        externalLinks: [],
        body: [
            '# Glossary',
            '',
            '- **Cycle** — one full guest visit, check-in to reset.',
            '- **E-STOP** — emergency stop button.',
            '- **Muster point** — the designated meet-up spot during evacuation.',
            '- **Save** — convincing a member not to cancel.',
        ].join('\n'),
    },
    ...seedLessonArticles(),
    ...seedGuideArticles(),
];

const seedAcks = () => [
    { id: 'ack-1', articleId: 'a-cancel-save', user_name: 'Alex Rivera', acknowledgedAt: iso(-2), articleVersion: 5 },
    { id: 'ack-2', articleId: 'a-cancel-save', user_name: 'Priya Patel', acknowledgedAt: iso(-1), articleVersion: 5 },
    { id: 'ack-3', articleId: 'a-safety', user_name: 'Alex Rivera', acknowledgedAt: iso(-20), articleVersion: 7 },
];

const seedAssignments = (user) => [
    {
        id: 'asn-1',
        articleId: 'a-safety',
        articleTitle: 'Safety & Emergency Procedures',
        assignee: user,
        dueDate: iso(-3),
        acknowledged: false,
        acknowledgedAt: null,
    },
    {
        id: 'asn-2',
        articleId: 'a-cancel-save',
        articleTitle: 'Cancellation-Save SOP',
        assignee: user,
        dueDate: iso(5),
        acknowledged: false,
        acknowledgedAt: null,
    },
    {
        id: 'asn-3',
        articleId: 'a-welcome',
        articleTitle: 'Welcome & First Shift Checklist',
        assignee: user,
        dueDate: iso(-10),
        acknowledged: true,
        acknowledgedAt: iso(-9),
    },
];

// --- Mutable store (lazy init) --------------------------------------------

let store = null;

const getStore = () => {
    if (!store) {
        const user = currentUser();
        store = {
            folders: seedFolders(),
            articles: seedArticles(),
            acks: seedAcks(),
            assignments: seedAssignments(user),
        };
    }
    return store;
};

export const MOCK_EMPLOYEES = [
    { value: 'Alex Rivera', label: 'Alex Rivera' },
    { value: 'Priya Patel', label: 'Priya Patel' },
    { value: 'Sam (HeroTrainer)', label: 'Sam (HeroTrainer)' },
    { value: 'HeroTrainer', label: 'Role: HeroTrainer' },
    { value: 'Manager', label: 'Role: Manager' },
];

const EDITOR_COMMANDS = [
    'add_knowledge_article',
    'update_knowledge_article',
    'delete_knowledge_article',
    'add_knowledge_folder',
    'delete_knowledge_folder',
    'assign_knowledge_article',
    'unassign_knowledge_article',
    'get_knowledge_article_acknowledgments',
    'preview_article_steps',
];

const READER_COMMANDS = [
    'get_knowledge_articles',
    'get_knowledge_article',
    'search_knowledge',
    'get_knowledge_folders',
    'get_my_knowledge',
    'acknowledge_knowledge_article',
    'flag_knowledge_article',
];

/** Learning commands (LMS contract section 6), so mock `get_allowed_commands` gates the Training UI the way the Lambda does. */
export const MOCK_LEARNER_COMMANDS = [
    'get_my_training',
    'get_courses',
    'get_course',
    'start_lesson',
    'submit_knowledge_check',
    'get_my_certifications',
    'get_certificate_upload_url',
    'record_certification',
];

export const MOCK_TRAINING_ADMIN_COMMANDS = [
    'get_training_matrix',
    'get_trainer_training',
    'pass_lesson_for_trainer',
    'waive_lesson_for_trainer',
    'reset_lesson_progress',
    'log_shadow_shift',
    'sign_off_competency',
    'verify_certification',
    'get_certificate_view_url',
    'save_course',
    'delete_course',
    'set_training_due',
    'export_training_matrix',
    'run_training_reminders',
];

/**
 * Mock mode has always answered as an editor. The lesson block is the first
 * place the server serves two shapes (answers stripped for non-editors), so
 * tests and the Training pages can flip the viewer to a trainer.
 */
let mockViewerIsEditor = true;
export const setMockViewerIsEditor = (value) => {
    mockViewerIsEditor = Boolean(value);
};
export const isMockViewerEditor = () => mockViewerIsEditor;

/**
 * Admin is the one rung above editor the mock has to tell apart:
 * `set_protected_code` is Admin-only on the server while managers edit
 * articles. Independent of the editor flag; tests flip it on its own.
 */
let mockViewerIsAdmin = true;
export const setMockViewerIsAdmin = (value) => {
    mockViewerIsAdmin = Boolean(value);
};
export const isMockViewerAdmin = () => mockViewerIsAdmin;

/** Mirror of `Knowledge._is_editor` serving: everyone but editors loses `answer` and `explanation`. */
const serveLesson = (lesson) => {
    if (!lesson || typeof lesson !== 'object') return lesson;
    if (mockViewerIsEditor) return lesson;
    const questions = (lesson.knowledgeCheck?.questions || []).map(({ answer, explanation, ...rest }) => rest);
    return { ...lesson, knowledgeCheck: { ...(lesson.knowledgeCheck || {}), questions } };
};

/** List rows drop the body and the step list but say which SOPs are guided. */
const summary = (a) => ({
    id: a.id,
    title: a.title,
    folderId: a.folderId,
    status: a.status,
    whenNeeded: a.whenNeeded,
    requiredReading: a.requiredReading,
    updatedAt: a.updatedAt,
    updatedBy: a.updatedBy,
    version: a.version,
    stepCount: (a.steps || []).length,
    lessonKind: a.lesson?.kind || null,
    minutes: Number(a.lesson?.minutes) || 0,
    questionCount: (a.lesson?.knowledgeCheck?.questions || []).length,
});

/** List rows for every article, the read the troubleshooting mock resolves its links from. */
export const mockArticleSummaries = () => getStore().articles.map(summary);

/**
 * Mirror of `Knowledge.load_article_steps`: the cross-module read the mock
 * shift flow and the task SOP checklist use. Null when the article is missing
 * or has no steps.
 */
export const mockLoadArticleSteps = (articleId) => {
    const article = getStore().articles.find((x) => x.id === articleId);
    const steps = normalizeSopSteps(article?.steps);
    if (!article || !steps.length) return null;
    return { articleId, title: article.title || '', version: Number(article.version) || 1, steps };
};

/**
 * Mirror of `Knowledge.load_lesson`: the unstripped block Learning grades
 * against. Null when the article is missing or carries no lesson.
 */
export const mockLoadLesson = (articleId) => {
    const article = getStore().articles.find((x) => x.id === articleId);
    if (!article || !article.lesson) return null;
    return {
        articleId,
        title: article.title || '',
        version: Number(article.version) || 1,
        status: article.status,
        visibility: article.visibility || [],
        lesson: article.lesson,
    };
};

/** Mirror of `Knowledge.acknowledge_article`, for a finished SOP walkthrough. */
export const mockAcknowledgeArticle = (articleId) => handlers.acknowledge_knowledge_article([articleId]);

const handlers = {
    get_allowed_commands: () => [
        ...READER_COMMANDS,
        ...MOCK_LEARNER_COMMANDS,
        ...(mockViewerIsEditor ? [...EDITOR_COMMANDS, ...MOCK_TRAINING_ADMIN_COMMANDS] : []),
        'get_allowed_commands',
    ],

    get_knowledge_articles: () =>
        getStore()
            .articles.map(summary)
            .sort((a, b) => (a.title || '').localeCompare(b.title || '')),

    get_knowledge_article: ([articleId]) => {
        const a = getStore().articles.find((x) => x.id === articleId);
        if (!a) throw new Error('Article not found.');
        const served = { ...a, steps: normalizeSopSteps(a.steps) };
        if (a.lesson) served.lesson = serveLesson(a.lesson);
        return served;
    },

    preview_article_steps: ([articleId]) => {
        const a = getStore().articles.find((x) => x.id === articleId);
        if (!a) throw new Error('Article not found.');
        return {
            articleId,
            steps: normalizeSopSteps(deriveStepsFromBody(a.body || '')),
            current: normalizeSopSteps(a.steps),
        };
    },

    search_knowledge: ([term]) => {
        const q = (term || '').toLowerCase();
        return getStore()
            .articles.filter(
                (a) =>
                    (a.title || '').toLowerCase().includes(q) ||
                    (a.body || '').toLowerCase().includes(q)
            )
            .map(summary);
    },

    get_knowledge_folders: () => getStore().folders.map((f) => ({ ...f })),

    get_my_knowledge: () => {
        const { assignments } = getStore();
        const now = Date.now();
        const acknowledged = assignments.filter((a) => a.acknowledged);
        const assigned = assignments.filter((a) => !a.acknowledged);
        const overdue = assigned.filter((a) => a.dueDate && new Date(a.dueDate).getTime() < now);
        return { assigned, acknowledged, overdue };
    },

    acknowledge_knowledge_article: ([articleId]) => {
        const s = getStore();
        const user = currentUser();
        const article = s.articles.find((x) => x.id === articleId);
        const already = s.acks.find((k) => k.articleId === articleId && k.user_name === user);
        if (!already) {
            s.acks.push({
                id: nextId('ack'),
                articleId,
                user_name: user,
                acknowledgedAt: iso(0),
                articleVersion: article ? article.version : 1,
            });
        }
        const asn = s.assignments.find((a) => a.articleId === articleId);
        if (asn) {
            asn.acknowledged = true;
            asn.acknowledgedAt = iso(0);
        }
        return { acknowledged: true };
    },

    flag_knowledge_article: ([articleId, note]) => ({ flagged: true, articleId, note: note || '' }),

    add_knowledge_article: ([payload]) => {
        const s = getStore();
        const article = {
            id: nextId('a'),
            version: 1,
            updatedAt: iso(0),
            updatedBy: currentUser(),
            externalLinks: [],
            visibility: [],
            ...payload,
            folderId: payload.folderId || 'ROOT',
            steps: normalizeSopSteps(payload.steps),
            // The Lambda runs `_clean_lesson` on every write; the mock stores the same shape.
            lesson: normalizeLesson(payload.lesson),
        };
        s.articles.push(article);
        return { ...article };
    },

    update_knowledge_article: ([articleId, payload]) => {
        const s = getStore();
        const idx = s.articles.findIndex((x) => x.id === articleId);
        if (idx === -1) throw new Error('Article not found.');
        const prev = s.articles[idx];
        const updated = {
            ...prev,
            ...payload,
            id: prev.id,
            // An editor that never sends `steps` keeps the stored list rather
            // than silently clearing a guided SOP.
            steps: 'steps' in payload ? normalizeSopSteps(payload.steps) : (prev.steps || []),
            // Same rule as `steps`: only a payload that names `lesson` changes it
            // (null clears), so an older editor cannot wipe a lesson block.
            lesson: 'lesson' in payload ? normalizeLesson(payload.lesson) : (prev.lesson || null),
            version: (prev.version || 1) + 1,
            updatedAt: iso(0),
            updatedBy: currentUser(),
        };
        s.articles[idx] = updated;
        return { ...updated };
    },

    delete_knowledge_article: ([articleId]) => {
        const s = getStore();
        s.articles = s.articles.filter((x) => x.id !== articleId);
        s.acks = s.acks.filter((k) => k.articleId !== articleId);
        s.assignments = s.assignments.filter((a) => a.articleId !== articleId);
        return { deleted: true };
    },

    add_knowledge_folder: ([name, parentId]) => {
        const s = getStore();
        const folder = { id: nextId('f'), name, parentId: parentId || null };
        s.folders.push(folder);
        return { ...folder };
    },

    delete_knowledge_folder: ([folderId]) => {
        const s = getStore();
        s.folders = s.folders.filter((f) => f.id !== folderId);
        return { deleted: true };
    },

    assign_knowledge_article: ([articleId, assignee, dueDate]) => {
        const s = getStore();
        const article = s.articles.find((x) => x.id === articleId);
        const assignment = {
            id: nextId('asn'),
            articleId,
            articleTitle: article ? article.title : articleId,
            assignee,
            dueDate: dueDate || '',
            acknowledged: false,
            acknowledgedAt: null,
        };
        s.assignments.push(assignment);
        return { ...assignment };
    },

    unassign_knowledge_article: ([articleId, assignee]) => {
        const s = getStore();
        s.assignments = s.assignments.filter(
            (a) => !(a.articleId === articleId && a.assignee === assignee)
        );
        return { unassigned: true };
    },

    get_knowledge_article_acknowledgments: ([articleId]) =>
        getStore()
            .acks.filter((k) => k.articleId === articleId)
            .map((k) => ({ ...k })),
};

/**
 * Drop-in replacement for `executeCommand`. Knowledge commands return a
 * { success, data } envelope object (which KnowledgeUtils.parseEnvelope reads
 * directly); `get_allowed_commands` returns a plain array like the real one.
 */
export const mockExecuteCommand = (command, args = []) =>
    new Promise((resolve, reject) => {
        setTimeout(() => {
            const handler = handlers[command];
            if (!handler) {
                resolve(JSON.stringify({ success: false, error: `Mock: unknown command "${command}".` }));
                return;
            }
            try {
                const data = handler(args || []);
                if (command === 'get_allowed_commands') {
                    resolve(data);
                } else {
                    resolve({ success: true, data });
                }
            } catch (e) {
                reject(e);
            }
        }, NETWORK_DELAY_MS);
    });
